import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Fade, Zoom, Slide, Bounce } from 'react-awesome-reveal'
import { Helmet } from 'react-helmet'

import {
    STATE_KEY_BACKGROUND,
    STATE_KEY_POSITION,
    STATE_KEY_BORDER,
    STATE_KEY_EFFECTS,
    TYPE_PADDING,
    TYPE_PADDING_MOBILE,
    TYPE_MARGIN,
    TYPE_BACKGROUND_COLOR,
    TYPE_BACKGROUND_COLOR_OPACITY,
    TYPE_BACKGROUND_IMAGE,
    TYPE_BACKGROUND_VIDEO,
    TYPE_BACKGROUND_POSITION,
    TYPE_BACKGROUND_SIZE,
    TYPE_BACKGROUND_REPEAT,
    TYPE_BACKGROUND_MODE,
    TYPE_BACKGROUND_LOADING_METHOD,
    TYPE_BACKGROUND_ALT_TEXT,
    TYPE_BORDER_X,
    TYPE_BORDER_Y,
    TYPE_BORDER_COLOR,
    TYPE_BORDER_WIDTH,
    TYPE_BORDER_STYLE,
    TYPE_ANIMATION_STYLE,
    TYPE_ANIMATION_DELAY,
    TYPE_ANIMATION_DURATION,
    TYPE_ANIMATION_HOVER,
    BACKGROUND_MODE_VIDEO_FLAG,
    TYPE_BOX_SHADOW,
    TYPE_ANCHOR_ID,
    STATE_KEY_ANCHOR,
} from '../const'

import * as S from './styled'

const map = (obj, fn) =>
    Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]))

const Components = {
    fade: Fade,
    zoom: Zoom,
    slide: Slide,
    bounce: Bounce,
    default: ({ children, triggerOnce, ...props }) => <div {...props}>{children}</div>,
}

const getComponentForAnimation = animation => {
    if (animation === 'none') {
        return {
            component: 'default',
            direction: '',
        }
    }

    const values = animation.split('-')

    return {
        component: values[0],
        direction: values[1] ? values[1] : null,
    }
}

const BlockDecorator = ({ children, isInsertMode, isLayoutMode, isPreviewMode, state, name }) => {
    const [styles, setStyles] = useState()
    const [highlight, setHighlight] = useState(false)
    const [isLayoutPlugin, setIsLayoutPlugin] = useState()

    useEffect(() => {
        const {
            [STATE_KEY_BACKGROUND]: backgroundValues = undefined,
            [STATE_KEY_POSITION]: positionValues = undefined,
            [STATE_KEY_BORDER]: borderValues = undefined,
            [STATE_KEY_EFFECTS]: effectValues = undefined,
            [STATE_KEY_ANCHOR]: anchorValues = undefined,
        } = state

        let styles = {}

        if (positionValues) {
            const {
                [TYPE_PADDING]: padding,
                [TYPE_MARGIN]: margin,
                [TYPE_PADDING_MOBILE]: paddingMobile,
            } = positionValues

            styles[TYPE_PADDING] = map(padding, v => (v || {}).value || 0)
            styles[TYPE_MARGIN] = map(margin, v => (v || {}).value || 0)
            styles[TYPE_PADDING_MOBILE] = paddingMobile
        }

        if (backgroundValues) {
            styles[TYPE_BACKGROUND_COLOR] = backgroundValues[TYPE_BACKGROUND_COLOR]
            styles[TYPE_BACKGROUND_COLOR_OPACITY] = backgroundValues[TYPE_BACKGROUND_COLOR_OPACITY]
            styles[TYPE_BACKGROUND_IMAGE] = backgroundValues[TYPE_BACKGROUND_IMAGE]
            styles[TYPE_BACKGROUND_VIDEO] = backgroundValues[TYPE_BACKGROUND_VIDEO]
            styles[TYPE_BACKGROUND_POSITION] = backgroundValues[TYPE_BACKGROUND_POSITION]
            styles[TYPE_BACKGROUND_SIZE] = backgroundValues[TYPE_BACKGROUND_SIZE]
            styles[TYPE_BACKGROUND_REPEAT] = backgroundValues[TYPE_BACKGROUND_REPEAT]
            styles[TYPE_BACKGROUND_MODE] = backgroundValues[TYPE_BACKGROUND_MODE]
            styles[TYPE_BACKGROUND_LOADING_METHOD] =
                backgroundValues[TYPE_BACKGROUND_LOADING_METHOD]
            styles[TYPE_BACKGROUND_ALT_TEXT] = backgroundValues[TYPE_BACKGROUND_ALT_TEXT]
        }

        if (borderValues) {
            styles[TYPE_BORDER_X] = borderValues[TYPE_BORDER_X]
            styles[TYPE_BORDER_Y] = borderValues[TYPE_BORDER_Y]
            styles[TYPE_BORDER_COLOR] = borderValues[TYPE_BORDER_COLOR]
            styles[TYPE_BORDER_WIDTH] = borderValues[TYPE_BORDER_WIDTH]
            styles[TYPE_BORDER_STYLE] = borderValues[TYPE_BORDER_STYLE]
        }

        if (effectValues) {
            styles[TYPE_ANIMATION_STYLE] = effectValues[TYPE_ANIMATION_STYLE]
            styles[TYPE_ANIMATION_DELAY] = effectValues[TYPE_ANIMATION_DELAY]
            styles[TYPE_ANIMATION_DURATION] = effectValues[TYPE_ANIMATION_DURATION]
            styles[TYPE_ANIMATION_HOVER] = effectValues[TYPE_ANIMATION_HOVER]
            styles[TYPE_BOX_SHADOW] = effectValues[TYPE_BOX_SHADOW]
        }

        if (anchorValues) {
            styles[TYPE_ANCHOR_ID] = anchorValues[TYPE_ANCHOR_ID]
        }

        setStyles(styles)
    }, [state])

    useEffect(() => {
        setHighlight(isInsertMode || isLayoutMode)
    }, [isInsertMode, isLayoutMode])

    useEffect(() => {
        setIsLayoutPlugin(name.startsWith('peracto/layout'))
    }, [name])

    const animationStyle =
        styles && styles[TYPE_ANIMATION_STYLE] ? styles[TYPE_ANIMATION_STYLE] : 'none'
    const hoverStyle =
        styles && styles[TYPE_ANIMATION_HOVER] ? styles[TYPE_ANIMATION_HOVER] : 'none'
    const anim = getComponentForAnimation(animationStyle)
    const AnimationElement = isPreviewMode ? Components[anim.component] : Components['default']

    const preloadImage =
        styles &&
        styles[TYPE_BACKGROUND_IMAGE] &&
        styles[TYPE_BACKGROUND_LOADING_METHOD] === 'preload'

    return (
        <>
            <AnimationElement
                fraction={0.2}
                triggerOnce={true}
                delay={styles && styles[TYPE_ANIMATION_DELAY]}
                duration={styles && styles[TYPE_ANIMATION_DURATION]}
                direction={anim.direction}
            >
                <div
                    data-testid={`${/[^/]*$/
                        .exec(name)[0]
                        .toLowerCase()
                        .replaceAll(' ', '-')}-component`}
                >
                    <S.ComponentWrapper
                        {...styles}
                        isPreviewMode={isPreviewMode}
                        className={classNames('component-wrapper', {
                            [`${hoverStyle}`]: hoverStyle !== 'none' && isPreviewMode,
                        })}
                        {...(styles?.[TYPE_ANCHOR_ID] && { id: styles[TYPE_ANCHOR_ID] })}
                        {...(styles?.[TYPE_BACKGROUND_IMAGE] &&
                            styles[TYPE_BACKGROUND_ALT_TEXT]?.length > 0 && {
                                role: 'img',
                                'aria-label': styles[TYPE_BACKGROUND_ALT_TEXT],
                            })}
                    >
                        {styles && styles[TYPE_BACKGROUND_MODE] === BACKGROUND_MODE_VIDEO_FLAG && (
                            <S.ComponentVideo
                                playsInline
                                autoPlay
                                loop
                                muted
                                src={styles[TYPE_BACKGROUND_VIDEO]}
                                poster={styles[TYPE_BACKGROUND_IMAGE]}
                            />
                        )}
                        {children}

                        {!isPreviewMode && (
                            <S.ComponentBorder
                                className="component-border"
                                highlight={highlight}
                                isLayoutPlugin={isLayoutPlugin}
                            />
                        )}
                    </S.ComponentWrapper>
                </div>
            </AnimationElement>

            {preloadImage && (
                <Helmet>
                    <link rel="preload" href={styles[TYPE_BACKGROUND_IMAGE]} as="image" />
                </Helmet>
            )}
        </>
    )
}

export default BlockDecorator
